import CustomButton from '@/components/custom-button/custom-btn.component';
import React from 'react';
import StudentTranscriptModal from './StudentTranscriptModal';
import useSWR from 'swr';
import { useUrlState } from '@/utils/useUrlState';
import { useDebounceState } from '@/utils/useDebounceState';
import { getData } from '@/api/common';
import { getQueryString } from '@/utils/common.util';
import Combobox from '@/components/combobox/Combobox';
import MultiSelectDropdown from '@/components/multi-select-dropdown/MultiSelectDropdown';
import PageLayout from '@/components/PageLayout';

const initialData = {
  studentId: '',
  academicYearIds: '',
};

export default function StudentTranscriptPage() {
  const pageTitle = 'Student Transcript';

  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = useUrlState(initialData);
  const [transcriptData, setTranscriptData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );

  const selectedStudent = query.studentId
    ? students?.find((s) => s.id === Number(query.studentId))
    : null;

  const selectedYearIds = query.academicYearIds
    ? query.academicYearIds.split(',').filter(Boolean)
    : [];

  const queryString = getQueryString({
    studentId: query.studentId,
    academicYearIds: selectedYearIds.join(','),
  });

  const handleViewTranscript = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getData(`academic/transcript?${queryString}`);
      if (response.success) {
        setTranscriptData(response.data);
        setIsOpen(true);
      } else {
        setError(response.message || 'Failed to load transcript');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageLayout
        breadcrumbs={[
          { label: 'Academics', to: '/academic' },
          { label: pageTitle, active: true },
        ]}
        title={pageTitle}
      >
        <div className="mt-20 no-print">
          <div className="card-row-2">
            <Combobox
              options={students ?? []}
              label="Select a Student"
              value={selectedStudent}
              onChange={(option) => {
                setQuery({
                  studentId: option ? option.id : '',
                });
                setSearchStudent('');
              }}
              onSearch={setSearchStudent}
              onClear={() => {
                setQuery({ studentId: '' });
                setSearchStudent('');
              }}
              loading={isStudentsLoading}
              renderSelected={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              renderOption={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              labelKey="fullName"
              valueKey="id"
              searchPlaceholder="Search students..."
              placeholder=""
            />

            <MultiSelectDropdown
              label="Academic Year"
              options={academicYears ?? []}
              selectedValues={selectedYearIds}
              onChange={(next) => setQuery({ academicYearIds: next.join(',') })}
              placeholder="Select academic years..."
              selectAllLabel="Select All Years"
              idKey="id"
              labelKey="description"
            />
          </div>

          {error && <p className="text-14 text-danger mt-10">{error}</p>}

          <CustomButton
            classes="outline primary font-bold mt-20"
            onClick={handleViewTranscript}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'View'}
          </CustomButton>
        </div>

        <StudentTranscriptModal
          isOpen={isOpen}
          transcriptData={transcriptData}
          student={selectedStudent}
          closeModal={() => setIsOpen(false)}
        />
      </PageLayout>
    </>
  );
}
